import React, { createContext, useReducer, useContext, useEffect } from 'react';
import { gameAPI } from '../services/api';
import { getTodayInActiveTimezone } from '../utils/timezone';

const GameContext = createContext();

const GAME_STATE_KEY = 'wordle6-game-state';

const initialState = {
  guesses: [],
  currentGuess: '',
  gameState: 'playing', // 'playing', 'won', 'lost'
  evaluations: [],
  gameNumber: null,
  error: null,
  isLoading: false,
  targetWord: null, // Only set when game is won/lost
};

function loadGameState() {
  try {
    const saved = localStorage.getItem(GAME_STATE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Check if it's from today in user's timezone
      const today = getTodayInActiveTimezone();
      if (parsed.date === today) {
        return { ...initialState, ...parsed.state };
      }
    }
  } catch (error) {
    console.error('Error loading game state:', error);
  }
  return initialState;
}

function saveGameState(state) {
  try {
    const today = getTodayInActiveTimezone();
    localStorage.setItem(GAME_STATE_KEY, JSON.stringify({
      date: today,
      state: {
        guesses: state.guesses,
        evaluations: state.evaluations,
        gameState: state.gameState,
        gameNumber: state.gameNumber,
        targetWord: state.targetWord,
      }
    }));
  } catch (error) {
    console.error('Error saving game state:', error);
  }
}

function gameReducer(state, action) {
  switch (action.type) {
    case 'SET_GAME_NUMBER':
      return { ...state, gameNumber: action.payload, isLoading: false };

    case 'ADD_LETTER':
      if (state.currentGuess.length < 6 && state.gameState === 'playing') {
        return { ...state, currentGuess: state.currentGuess + action.letter };
      }
      return state;

    case 'REMOVE_LETTER':
      if (state.currentGuess.length > 0) {
        return { ...state, currentGuess: state.currentGuess.slice(0, -1) };
      }
      return state;

    case 'SUBMIT_GUESS': {
      const newState = {
        ...state,
        guesses: [...state.guesses, state.currentGuess],
        currentGuess: '',
        evaluations: [...state.evaluations, action.evaluation],
        gameState: action.gameState,
        targetWord: action.targetWord || state.targetWord,
        error: null,
      };
      saveGameState(newState);
      return newState;
    }

    case 'SET_ERROR':
      return { ...state, error: action.message };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    case 'CLEAR_GUESS':
      return { ...state, currentGuess: '' };

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'RESET_GAME':
      const resetState = { ...initialState, gameNumber: state.gameNumber };
      saveGameState(resetState);
      return resetState;

    case 'SYNC_FROM_STORAGE':
      // Sync state from another tab's localStorage update
      // Don't save to avoid infinite loop
      // Only clear currentGuess if there are new guesses (another tab submitted)
      const hasNewGuesses = action.payload.guesses.length > state.guesses.length;

      return {
        ...state,
        guesses: action.payload.guesses,
        evaluations: action.payload.evaluations,
        gameState: action.payload.gameState,
        gameNumber: action.payload.gameNumber,
        targetWord: action.payload.targetWord,
        currentGuess: hasNewGuesses ? '' : state.currentGuess, // Only clear if new guesses
      };

    default:
      return state;
  }
}

export function GameProvider({ children }) {
  const [state, dispatch] = useReducer(gameReducer, initialState, loadGameState);

  // Validate game number on mount if we have a cached one
  useEffect(() => {
    const validateGameNumber = async () => {
      // Only validate if we loaded a game number from cache
      if (state.gameNumber !== null) {
        try {
          const data = await gameAPI.getDailyWord();
          if (state.gameNumber !== data.game_number) {
            console.warn(`Game number mismatch on load. Local: ${state.gameNumber}, Backend: ${data.game_number}. Resetting.`);
            dispatch({ type: 'RESET_GAME' });
          }
        } catch (error) {
          console.error('Error validating game number:', error);
        }
      }
    };

    validateGameNumber();
  }, []); // Run once on mount

  // Fetch daily word info on mount and when date changes
  useEffect(() => {
    async function fetchDailyWord(retryCount = 0) {
      const maxRetries = 5;
      const retryDelays = [0, 3000, 5000, 10000, 15000]; // Delays between retries

      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        if (retryCount > 0) {
          dispatch({ type: 'SET_ERROR', message: `Server is waking up... (attempt ${retryCount + 1}/${maxRetries + 1})` });
        }

        const data = await gameAPI.getDailyWord();

        // Check if loaded game number matches backend
        if (state.gameNumber !== null && state.gameNumber !== data.game_number) {
          console.warn(`Game number mismatch detected. Local: ${state.gameNumber}, Backend: ${data.game_number}. Resetting game state.`);
          dispatch({ type: 'RESET_GAME' });
        }

        dispatch({ type: 'CLEAR_ERROR' });
        dispatch({ type: 'SET_GAME_NUMBER', payload: data.game_number });
      } catch (error) {
        console.error('Error fetching daily word:', error);

        // Retry if we haven't exceeded max retries
        if (retryCount < maxRetries) {
          const delay = retryDelays[retryCount] || 10000;
          console.log(`Retrying in ${delay}ms... (attempt ${retryCount + 1}/${maxRetries})`);
          setTimeout(() => fetchDailyWord(retryCount + 1), delay);
        } else {
          dispatch({ type: 'SET_ERROR', message: 'Failed to load daily puzzle. Please refresh the page.' });
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      }
    }

    // Always check if we need to fetch/validate game number
    const today = getTodayInActiveTimezone();
    const saved = localStorage.getItem(GAME_STATE_KEY);

    // Fetch if we don't have a game number yet
    if (state.gameNumber === null) {
      fetchDailyWord();
      return;
    }

    // Also check if the date has changed (user kept tab open overnight)
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.date && parsed.date !== today) {
          // Date changed, need to reset and fetch new game number
          console.log('Date changed, resetting game');
          dispatch({ type: 'RESET_GAME' });
        }
      } catch (error) {
        console.error('Error checking saved date:', error);
      }
    }
  }, [state.gameNumber]);

  // Save state when it changes
  useEffect(() => {
    if (state.gameNumber !== null) {
      saveGameState(state);
    }
  }, [state.guesses, state.gameState, state.gameNumber]);

  // Periodically check if date has changed (for users who keep tab open)
  useEffect(() => {
    const checkDateChange = () => {
      const today = getTodayInActiveTimezone();
      const saved = localStorage.getItem(GAME_STATE_KEY);

      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed.date && parsed.date !== today && state.gameNumber !== null) {
            // Date changed, reset the game
            dispatch({ type: 'RESET_GAME' });
          }
        } catch (error) {
          console.error('Error in date check:', error);
        }
      }
    };

    // Check every 60 seconds
    const interval = setInterval(checkDateChange, 60000);
    return () => clearInterval(interval);
  }, [state.gameNumber]);

  // Listen for storage changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Only respond to changes to our game state key
      if (e.key !== GAME_STATE_KEY || !e.newValue) return;

      try {
        const parsed = JSON.parse(e.newValue);
        const today = getTodayInActiveTimezone();

        // Only sync if it's from today
        if (parsed.date === today && parsed.state) {
          // Defensive check: Don't sync if the data is identical to our current state
          // This prevents iOS Chrome from syncing its own changes back to itself
          const isSameData =
            JSON.stringify(state.guesses) === JSON.stringify(parsed.state.guesses) &&
            JSON.stringify(state.evaluations) === JSON.stringify(parsed.state.evaluations) &&
            state.gameState === parsed.state.gameState;

          if (!isSameData) {
            console.log('Syncing state from another tab', {
              currentGuesses: state.guesses.length,
              incomingGuesses: parsed.state.guesses.length
            });
            dispatch({
              type: 'SYNC_FROM_STORAGE',
              payload: parsed.state
            });
          }
        }
      } catch (error) {
        console.error('Error syncing from storage:', error);
      }
    };

    // The storage event only fires when OTHER tabs/windows make changes
    // Note: iOS Chrome may sometimes fire this for the same tab, so we have defensive checks
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [state.guesses, state.evaluations, state.gameState]);

  return (
    <GameContext.Provider value={{ state, dispatch }}>
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
}